
import React, { useState, useEffect } from "react";
import { X, Upload, RotateCw, Crop, Copy, Layers, AlignCenter, FlipHorizontal, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Toggle } from "@/components/ui/toggle";
import { useToast } from "@/hooks/use-toast";
import { constrainRotation } from "../utils/designEditUtils";

interface DesignEditorPanelProps {
  onClose: () => void;
  designSize: number;
  setDesignSize: (size: number) => void;
  rotation: number;
  setRotation: (rotation: number) => void;
  designPosition: { x: number, y: number };
  setDesignPosition: (position: { x: number, y: number }) => void;
  onSaveDesign: () => void;
  onResetDesign: () => void;
  onRemoveDesign: () => void;
}

const DesignEditorPanel: React.FC<DesignEditorPanelProps> = ({
  onClose,
  designSize,
  setDesignSize,
  rotation,
  setRotation,
  designPosition,
  setDesignPosition,
  onSaveDesign,
  onResetDesign,
  onRemoveDesign
}) => {
  const { toast } = useToast();
  const [sizeInputWidth, setSizeInputWidth] = useState("10.40");
  const [sizeInputHeight, setSizeInputHeight] = useState("10.40");
  const [isFullColor, setIsFullColor] = useState(true);
  
  // Update size inputs when designSize changes
  useEffect(() => {
    const newSize = (designSize * 13).toFixed(2);
    setSizeInputWidth(newSize);
    setSizeInputHeight(newSize);
  }, [designSize]);
  
  const handleSizeWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSizeInputWidth(value);
    // Also update the design size relatively
    const newSize = parseFloat(value) / 13;
    if (!isNaN(newSize)) {
      setDesignSize(Math.max(0.3, Math.min(1.5, newSize)));
    }
  };
  
  const handleSizeHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSizeInputHeight(value);
    // Also update the design size relatively
    const newSize = parseFloat(value) / 13;
    if (!isNaN(newSize)) {
      setDesignSize(Math.max(0.3, Math.min(1.5, newSize)));
    }
  };
  
  const handleRotationChange = (value: number[]) => {
    setRotation(value[0]);
  };
  
  const handleRotationInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      // Fix: Use constrainRotation directly instead of modifying with %
      setRotation(constrainRotation(value));
    }
  };
  
  const showComingSoon = () => {
    toast({
      title: "Coming Soon!",
      description: "This feature is not yet available but will be coming soon.",
    });
  };

  const handleCenterDesign = () => {
    setDesignPosition({ x: 50, y: 40 });
    toast({
      title: "Design centered",
      description: "Your design has been centered on the shirt.",
    });
  };
  
  const handleFlipDesign = () => {
    // Fix: Use setRotation directly with the constraint function
    setRotation(constrainRotation(rotation + 180));
    toast({
      title: "Design flipped",
      description: "Your design has been flipped horizontally.",
    });
  };
  
  return (
    <div className="bg-white p-4 w-full max-w-md border rounded-md shadow-md">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium">Edit Upload</h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>
      
      <div className="space-y-4">
        {/* Size Controls */}
        <div>
          <label className="text-sm font-medium mb-1 block">Upload Size</label>
          <div className="text-xs text-gray-500 mb-1">Width × Height</div>
          <div className="flex items-center gap-2">
            <Input 
              value={sizeInputWidth} 
              onChange={handleSizeWidthChange} 
              className="w-24"
            />
            <span className="text-xs">in</span>
            <span className="text-gray-500">×</span>
            <Input 
              value={sizeInputHeight} 
              onChange={handleSizeHeightChange} 
              className="w-24"
            />
            <span className="text-xs">in</span>
          </div>
        </div>
        
        {/* Color Controls */}
        <div>
          <label className="text-sm font-medium mb-1 block">Color</label>
          <div className="flex justify-between items-center">
            <span className="text-sm">Full Color</span>
            <Toggle 
              pressed={isFullColor} 
              onPressedChange={setIsFullColor}
              aria-label="Toggle full color"
            />
          </div>
          <div className="flex items-center mt-2">
            <span className="text-sm">Make One Color</span>
            <span className="text-xs text-red-500 ml-1">New!</span>
          </div>
        </div>
        
        {/* Tool Controls */}
        <div className="grid grid-cols-3 gap-2">
          <div 
            className="flex flex-col items-center border rounded p-2 cursor-pointer hover:bg-gray-50"
            onClick={handleCenterDesign}
          >
            <AlignCenter className="h-5 w-5 mb-1" />
            <span className="text-xs">Center</span>
          </div>
          <div 
            className="flex flex-col items-center border rounded p-2 cursor-pointer hover:bg-gray-50"
            onClick={() => showComingSoon()}
          >
            <Layers className="h-5 w-5 mb-1" />
            <span className="text-xs">Layering</span>
          </div>
          <div 
            className="flex flex-col items-center border rounded p-2 cursor-pointer hover:bg-gray-50"
            onClick={handleFlipDesign}
          >
            <FlipHorizontal className="h-5 w-5 mb-1" />
            <span className="text-xs">Flip</span>
          </div>
          <div 
            className="flex flex-col items-center border rounded p-2 cursor-pointer hover:bg-gray-50"
            onClick={() => showComingSoon()}
          >
            <Copy className="h-5 w-5 mb-1" />
            <span className="text-xs">Duplicate</span>
          </div>
          <div 
            className="flex flex-col items-center border rounded p-2 cursor-pointer hover:bg-gray-50"
            onClick={() => showComingSoon()}
          >
            <Crop className="h-5 w-5 mb-1" />
            <span className="text-xs">Crop</span>
          </div>
          <div 
            className="flex flex-col items-center border rounded p-2 cursor-pointer hover:bg-gray-50 text-red-500"
            onClick={onRemoveDesign}
          >
            <Trash2 className="h-5 w-5 mb-1" />
            <span className="text-xs">Remove</span>
          </div>
        </div>
        
        {/* Rotation Control */}
        <div>
          <label className="text-sm font-medium mb-1 block">Rotation</label>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Slider
                value={[rotation]}
                min={0}
                max={359}
                step={1}
                onValueChange={handleRotationChange}
              />
            </div>
            <Input 
              value={rotation.toString()} 
              onChange={handleRotationInputChange} 
              className="w-16 text-center"
            />
          </div>
        </div>
        
        {/* Position Controls */}
        <div>
          <label className="text-sm font-medium mb-1 block">Position</label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500">Horizontal (X)</label>
              <Slider
                value={[designPosition.x]}
                min={20}
                max={80}
                step={1}
                onValueChange={(values) => setDesignPosition({ ...designPosition, x: values[0] })}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Vertical (Y)</label>
              <Slider
                value={[designPosition.y]}
                min={20}
                max={70}
                step={1}
                onValueChange={(values) => setDesignPosition({ ...designPosition, y: values[0] })}
              />
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onResetDesign}>
            Reset To Original
          </Button>
          <Button onClick={onSaveDesign} className="bg-blue-600 hover:bg-blue-700 text-white">
            Save Design
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DesignEditorPanel;
